import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  FormSelect,
  FormTextarea,
  SkeletonCard,
} from "@/components/ui";
import { apiClient, getErrorMessage, isApiError } from "@/api/client";
import { centsToUsd } from "@/lib/money";
import { usePublicClassSections, usePublicTerms, useRegisterForClass } from "./useClassRegistrationPublic";
import {
  ClassRegistrationFormSchema,
  EMPTY_CLASS_REGISTRATION_FORM_VALUES,
  toPublicRegistrationInput,
  type ClassRegistrationFormValues,
} from "./classRegistrationForm";
import { AGE_BAND_LABELS, PAYMENT_CHANNEL_LABELS, type AgeBand, type PublicRegistrationResponse } from "@/api/types/classRegistration";
import { formatSeatsRemaining, formatWeekdaySlot, sectionDisplayName } from "@/features/classes/classFormat";

const BUSINESS_NAME = "The Fox & The Owl";

const AGE_BANDS: AgeBand[] = ["age_4_6", "age_7_9", "age_10_12"];

const RATE_LIMIT_MESSAGE = "Too many attempts — please try again in a minute.";

/**
 * P3-b: the three consent copy blocks used to be hardcoded here verbatim
 * from app/services/legal_consent.py — two sources of truth for
 * attorney-reviewed compliance language that could silently drift apart
 * on a future wording edit. This page now fetches them from the public,
 * rate-limited GET /api/public/class-registration/consent-texts endpoint
 * and renders exactly what the server returns, including which
 * CONSENT_TEXT_VERSION the guardian is agreeing to (submitted back on
 * register — the server always stamps the enrollment with its OWN
 * current version regardless, this is audit-trail only).
 */
interface PublicConsentTexts {
  liability_waiver_text: string;
  statutory_disclosure_text: string;
  media_release_text: string;
  consent_text_version: number;
}

function useConsentTexts() {
  return useQuery<PublicConsentTexts>({
    queryKey: ["public-class-registration", "consent-texts"],
    queryFn: async () => {
      const { data } = await apiClient.get<PublicConsentTexts>("/public/class-registration/consent-texts");
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * PUBLIC class-registration page (Phase 2c) — reached at /register-classes,
 * no auth, no AppShell (see App.tsx's public routes list, same tier as
 * /portal and /membership-portal). A single continuous form (not a hard
 * wizard): pick a term + section, guardian/child info, payment channel,
 * then the three legal consent controls, in that order top to bottom.
 *
 * HARD LEGAL REQUIREMENT: never use "childcare"/"drop-off care" anywhere
 * on this page — "classes"/"instruction" only. The three consent copy
 * blocks above are verbatim from the owner's Texas single-skill-exemption
 * compliance language and must not be paraphrased.
 */
export function ClassRegistrationPage() {
  const [termId, setTermId] = useState("");
  const [ageBandFilter, setAgeBandFilter] = useState<AgeBand | "">("");
  const [result, setResult] = useState<PublicRegistrationResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: terms, isLoading: termsLoading, error: termsError } = usePublicTerms();
  const { data: sections, isLoading: sectionsLoading } = usePublicClassSections({
    term_id: termId || undefined,
    age_band: ageBandFilter || undefined,
  });
  const { data: consentTexts, isLoading: consentTextsLoading, error: consentTextsError } = useConsentTexts();
  const registerForClass = useRegisterForClass();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClassRegistrationFormValues>({
    resolver: zodResolver(ClassRegistrationFormSchema),
    defaultValues: EMPTY_CLASS_REGISTRATION_FORM_VALUES,
  });

  const selectedSectionId = watch("section_id");

  // Default to the first available term once terms load.
  useEffect(() => {
    if (termId || !terms || terms.length === 0) return;
    setTermId(terms[0].id);
  }, [terms, termId]);

  // Changing the term (or the age-band filter) invalidates whichever
  // section was selected under the old list.
  useEffect(() => {
    setValue("section_id", "");
  }, [termId, ageBandFilter, setValue]);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const payload = {
        ...toPublicRegistrationInput(values),
        consent_text_version: consentTexts?.consent_text_version,
      };
      const response = await registerForClass.mutateAsync(payload);
      setResult(response);
      reset(EMPTY_CLASS_REGISTRATION_FORM_VALUES);
    } catch (err) {
      if (isApiError(err) && err.response?.status === 429) {
        setSubmitError(RATE_LIMIT_MESSAGE);
      } else {
        setSubmitError(getErrorMessage(err));
      }
    }
  });

  return (
    <div className="min-h-screen bg-bg-body px-4 py-8 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <header className="text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-wide text-copper">{BUSINESS_NAME}</p>
          <h1 className="mt-1 font-display text-2xl font-bold text-text-primary sm:text-3xl">Register for classes</h1>
        </header>

        {result ? (
          <RegistrationConfirmation result={result} onRegisterAnother={() => setResult(null)} />
        ) : (
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>1. Choose a class</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {termsLoading ? (
                  <SkeletonCard />
                ) : termsError ? (
                  <ApiError error={termsError} title="Unable to load terms" />
                ) : !terms || terms.length === 0 ? (
                  <p className="text-sm text-text-secondary">No terms are open for registration right now.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormSelect label="Term" value={termId} onChange={(e) => setTermId(e.target.value)}>
                        {terms.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </FormSelect>
                      <FormSelect
                        label="Age (optional)"
                        value={ageBandFilter}
                        onChange={(e) => setAgeBandFilter(e.target.value as AgeBand | "")}
                      >
                        <option value="">Any age</option>
                        {AGE_BANDS.map((b) => (
                          <option key={b} value={b}>
                            {AGE_BAND_LABELS[b]}
                          </option>
                        ))}
                      </FormSelect>
                    </div>

                    {sectionsLoading ? (
                      <SkeletonCard />
                    ) : !sections || sections.length === 0 ? (
                      <p className="text-sm text-text-secondary">No classes match this term/age combination.</p>
                    ) : (
                      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Available classes">
                        {sections.map((section) => (
                          <label
                            key={section.id}
                            className={`flex cursor-pointer flex-col gap-1 rounded-md border px-3 py-2.5 text-sm transition-colors sm:flex-row sm:items-center sm:justify-between ${
                              selectedSectionId === section.id
                                ? "border-primary bg-primary/5 ring-1 ring-primary"
                                : "border-border hover:border-border-strong"
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <input type="radio" value={section.id} className="accent-primary" {...register("section_id")} />
                              <span>
                                <span className="block font-medium text-text-primary">{sectionDisplayName(section)}</span>
                                <span className="block text-xs text-text-muted">
                                  {formatWeekdaySlot(section.weekday, section.slot)}
                                  {section.age_band ? ` · ${AGE_BAND_LABELS[section.age_band]}` : ""}
                                </span>
                              </span>
                            </span>
                            <span className="flex items-center gap-3 pl-6 sm:pl-0">
                              <span className="font-semibold text-text-primary">
                                {centsToUsd(section.price_cents_monthly)}/mo
                              </span>
                              <span className={section.seats_remaining > 0 ? "text-text-muted" : "text-warning"}>
                                {formatSeatsRemaining(section.seats_remaining, section.waitlist_open)}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                    {errors.section_id && (
                      <p className="text-sm text-danger" role="alert">
                        {errors.section_id.message}
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>2. Guardian & child info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField label="Your name" required error={errors.guardian_name?.message} {...register("guardian_name")} />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label="Email" type="email" error={errors.guardian_email?.message} {...register("guardian_email")} />
                  <FormField label="Phone" type="tel" error={errors.guardian_phone?.message} {...register("guardian_phone")} />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    label="Child's first name"
                    required
                    error={errors.child_first_name?.message}
                    {...register("child_first_name")}
                  />
                  <FormField
                    label="Child's last name"
                    required
                    error={errors.child_last_name?.message}
                    {...register("child_last_name")}
                  />
                </div>
                <FormField
                  label="Child's birthdate"
                  type="date"
                  required
                  error={errors.child_birthdate?.message}
                  {...register("child_birthdate")}
                />
                <FormTextarea label="Anything we should know? (optional)" {...register("notes")} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>3. How will you pay?</CardTitle>
              </CardHeader>
              <CardContent>
                <FormSelect
                  label="Payment channel"
                  required
                  error={errors.payment_channel?.message}
                  {...register("payment_channel")}
                >
                  <option value="">Select…</option>
                  <option value="direct">{PAYMENT_CHANNEL_LABELS.direct}</option>
                  <option value="esa_tefa">{PAYMENT_CHANNEL_LABELS.esa_tefa}</option>
                </FormSelect>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>4. Required acknowledgments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {consentTextsLoading ? (
                  <SkeletonCard />
                ) : consentTextsError || !consentTexts ? (
                  <ApiError error={consentTextsError} title="Unable to load the required consent text" />
                ) : (
                  <>
                    <div>
                      <label className="flex items-start gap-3 text-sm text-text-primary">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 shrink-0 rounded border-border accent-primary"
                          {...register("waiver_ack")}
                        />
                        <span>{consentTexts.liability_waiver_text}</span>
                      </label>
                      {errors.waiver_ack && (
                        <p className="mt-1 text-sm text-danger" role="alert">
                          {errors.waiver_ack.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="flex items-start gap-3 text-sm text-text-primary">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 shrink-0 rounded border-border accent-primary"
                          {...register("disclosure_ack")}
                        />
                        <span>{consentTexts.statutory_disclosure_text}</span>
                      </label>
                      {errors.disclosure_ack && (
                        <p className="mt-1 text-sm text-danger" role="alert">
                          {errors.disclosure_ack.message}
                        </p>
                      )}
                    </div>

                    <fieldset>
                      <legend className="mb-2 text-sm text-text-primary">{consentTexts.media_release_text}</legend>
                      <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                        <label className="flex items-center gap-2 text-sm text-text-primary">
                          <input type="radio" value="granted" className="accent-primary" {...register("media_release")} />
                          Yes, you may photograph/video my child
                        </label>
                        <label className="flex items-center gap-2 text-sm text-text-primary">
                          <input type="radio" value="declined" className="accent-primary" {...register("media_release")} />
                          No, please don't
                        </label>
                      </div>
                      {errors.media_release && (
                        <p className="mt-1 text-sm text-danger" role="alert">
                          {errors.media_release.message}
                        </p>
                      )}
                    </fieldset>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Honeypot — invisible to a real guardian, tabIndex -1 so it's
                never reached by keyboard navigation, but present in the DOM
                for a bot that auto-fills every field to trip. */}
            <div className="sr-only" aria-hidden="true">
              <label htmlFor="class-registration-website">Website</label>
              <input
                id="class-registration-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                {...register("website")}
              />
            </div>

            {submitError && (
              <p className="text-center text-sm text-danger" role="alert" data-testid="registration-error">
                {submitError}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={registerForClass.isPending || !consentTexts}
              data-testid="submit-registration"
            >
              {registerForClass.isPending ? "Submitting…" : "Submit registration"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function RegistrationConfirmation({
  result,
  onRegisterAnother,
}: {
  result: PublicRegistrationResponse;
  onRegisterAnother: () => void;
}) {
  const isWaitlisted = result.status === "waitlisted";

  return (
    <Card className="text-center" data-testid="registration-confirmation">
      <CardContent className="flex flex-col items-center gap-3 py-8">
        <span className="text-4xl" role="img" aria-label={isWaitlisted ? "On the waitlist" : "Enrolled"}>
          {isWaitlisted ? "⏳" : "🎉"}
        </span>
        {isWaitlisted ? (
          <>
            <p className="text-lg font-semibold text-text-primary">You're on the waitlist</p>
            <p className="max-w-sm text-sm text-text-secondary">
              Position {result.waitlist_position ?? "—"}. We'll reach out the moment a spot opens up.
            </p>
          </>
        ) : (
          <>
            <p className="text-lg font-semibold text-text-primary">You're enrolled!</p>
            <p className="max-w-sm text-sm text-text-secondary">
              Your monthly price is <span className="font-semibold text-text-primary">{centsToUsd(result.monthly_price_cents)}</span>
              {result.sibling_discount_applied ? " — includes a 10% sibling discount." : "."}
            </p>
            {result.billing_mode?.startsWith("manual-billing") && (
              <p className="max-w-sm text-xs text-text-muted">
                We'll follow up separately about payment for this enrollment.
              </p>
            )}
          </>
        )}
        <Button variant="secondary" size="sm" onClick={onRegisterAnother} className="mt-2">
          Register another child
        </Button>
      </CardContent>
    </Card>
  );
}
